import React, {useMemo, useRef} from 'react';
import PropTypes from 'prop-types';

import { Tabs, Tab } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { makeStyles } from '@material-ui/core/styles';

import { attributeOptions, formatNumber, getAttributeText } from '../../Utilities/Helpers';
import { getHazardNarrative } from '../../Utilities/Data';

import './Chart.scss';

import exporting from 'highcharts/modules/exporting';
import Highcharts from 'highcharts';
import NoDataToDisplay from 'highcharts/modules/no-data-to-display';
import HighchartsReact from 'highcharts-react-official';
import highchartsMore from 'highcharts/highcharts-more'; 
highchartsMore(Highcharts); 
exporting(Highcharts);
NoDataToDisplay(Highcharts);

Highcharts.setOptions({
  lang: {
    thousandsSep: ',',
    numericSymbols: ['k', 'M', 'B', 'T', 'Qd', 'Qt']
  }
});


const tabHeight = '37px';
const useStyles = makeStyles(() => ({
  tabsRoot: {
    minHeight: tabHeight,
    height: tabHeight,
    backgroundColor: 'rgba(0,0,0,0.06)',
    width: '100%',
  },
  tabRoot: {
    minHeight: tabHeight,
    height: tabHeight,
    maxWidth: '220px',
    borderBottom: '2px solid rgba(0,0,0,0.2)',
    borderRight: '1px solid rgba(0,0,0,0.1)',
    backgroundColor: 'rgba(0,0,0,0.06)',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.12)'
    }
  }
}));



export default function Chart({
  chartTab,
  chartVisible,
  climateOption,
  climateRange,
  countyClimateData,
  countyHazardData,
  currEmData,
  emissionMinMax,
  hazardOptions,
  hazardRange,
  loc,
  mode,
  stateClimateData,
  stateHazardData,
  windowHeight,
  windowWidth,
  downloadData,
  setEpisodes,
  handleOptionsChange
}) {
  const chartCont = useRef();
  const classes = useStyles();

  
  const handleTabChange = (event, newValue) => {
    handleOptionsChange(newValue, 'chartTab');
  };
  

  // Default object to set chart menu and data download function
  const exportObj = {
    enabled: true,
    buttons: {
      contextButton: {
        menuItems: ['viewFullscreen', 'printChart', 'separator', 'downloadPNG', 'downloadJPEG', 'downloadPDF', 'downloadSVG', 'separator', 'downloadData']
      }
    },
    menuItemDefinitions: {
      downloadData: {
        onclick: downloadData,
        text: 'Download Chart Data'
      }
    },
  };

  
  // Handles constructing the chart for climate history if mode === true && tab === 0
  const renderClimateHistoryChart = useMemo(() => {
    const startIndex = climateRange.start - climateRange.min + (attributeOptions[climateOption]['winter'] ? 1 : 0);
    const numOfYears = climateRange.end - climateRange.min + (attributeOptions[climateOption]['winter'] ? 2 : 1);

    if(loc) {
      let {attrName, unit, tooltipUnit} = getAttributeText(mode, climateOption);
      let years;
      let data;

      try {
        years = stateClimateData.years.slice(startIndex, numOfYears);
      } catch {
        years = [];
      }

      try {
        if(loc.id.length === 2) {
          data = stateClimateData[climateOption][loc.id].slice(startIndex, numOfYears);
        } else {
          data = countyClimateData[climateOption][loc.id].slice(startIndex, numOfYears);
        }
      } catch {
        data = [];
      }

      let options =  {
        exporting: exportObj,
        credits: {enabled: false},
        chart: {
          type: 'line',
          zoomType: 'x',
          height: chartCont.current.clientHeight,
          width: chartCont.current.clientWidth,
          backgroundColor: 'rgba(0, 0, 0, 0)'
        },
        legend: {
          floating: true,
          layout: 'vertical',
          align: 'left',
          verticalAlign: 'top',
          x: 50,
          y: 50,
          backgroundColor: 'white',
          borderRadius: 5,
          height: 60,
          shadow: true,
          itemWidth: 180,
          itemStyle: {
            textOverflow: 'wrap',
            fontSize: 12
          }
        },
        title: {
          text: `${attrName} in ${loc.state ? `${loc.name} County, ${loc.state}` : loc.name} from ${climateRange.start} to ${climateRange.end}`
        },
        series: [
          {data: data, name: attrName},
        ],
        xAxis: {
          categories: years,
          title: {
            text: 'Year'
          }
        },
        yAxis: {
          gridLineColor: '#a1a1a1',
          title: {
            text: unit
          },
          min: emissionMinMax[0],
          max: emissionMinMax[1],
        },
        tooltip: {
          formatter: function () {
            return `<b>${this.x}</b><br/>${attrName}: <b>${this.y.toFixed(2)} ${tooltipUnit}</b>`;
          }
        }
      };
      
      return <HighchartsReact highcharts={Highcharts} options={options} />;
    }
  }, [climateOption, climateRange, loc, stateClimateData, countyClimateData, windowWidth, windowHeight, emissionMinMax]);

  
  // Handles constructing the chart for climate projections if mode === true && tab !== 0
  const renderClimateEmissionChart = useMemo(() => {
    if (chartTab !== 0) {
      let {attrName, unit, tooltipUnit} = getAttributeText(mode, climateOption);

      let options = {
        exporting: exportObj,
        credits: {enabled: false},
        chart: {
          type: 'line',
          zoomType: 'x',
          height: chartCont.current.clientHeight,
          width: chartCont.current.clientWidth,
          backgroundColor: 'rgba(0, 0, 0, 0)'
        },
        legend: {
          floating: true,
          layout: 'vertical',
          align: 'left',
          verticalAlign: 'top',
          shadow: true,
          backgroundColor: 'white',
          x: 50,
          y: 50,
          borderRadius: 5,
          height: 60,
          itemWidth: 180,
          itemStyle: {
            textOverflow: 'wrap',
            fontSize: 12
          }
        },
        title: {
          text: `${attrName} in ${loc.state ? `${loc.name} County, ${loc.state}` : loc.name} from ${new Date().getFullYear()} to 2099 under a ${chartTab === 1 ? 'High' : 'Low'} Emissions Future Scenario (Model Data)`
        },
        series: [
          {
            marker: {
              enabled: false
            },
            type: 'arearange',
            data: currEmData.area.y,
            name: 'Climate Model Range'
          },{
            data: currEmData.mean,
            name: 'Climate Model Average'
          }
        ],
        xAxis: {
          categories: currEmData.area.x,
          title: {
            text: 'Year'
          }
        },
        yAxis: {
          gridLineColor: '#a1a1a1',
          title: {
            text: (unit && unit !== '') ? unit.slice(0,1).toUpperCase() + unit.slice(1) : 'USD'
          },
          min: emissionMinMax[0],
          max: emissionMinMax[1],
        },
        tooltip: {
          crosshairs: true,
          shared: true,
          formatter: function () {
            return this.points.reduce(function (s, point) {
              let name = point.series.name.split(' ')[2];
              return s + '<br/>' + name + ': ' +
                  `${(point.point.high !== undefined && point.point.low !== undefined) ? `<b>${point.point.low.toFixed(2)}</b>-<b>${point.point.high.toFixed(2)}</b>` : `<b>${point.y.toFixed(2)}</b>`} ${tooltipUnit}`;
            }, '<b>' + this.x + ' Climate Model</b>');
          }
        }
      };
      
      return <HighchartsReact highcharts={Highcharts} options={options} />;
    }
  }, [currEmData, climateOption, chartTab, emissionMinMax, loc, windowWidth, windowHeight]);
  

  // Handles constructing the chart for hazards if mode === false
  const renderHazardHistoryChart = useMemo(() => {
    const startIndex = hazardRange.start - hazardRange.min;
    const numOfYears = hazardRange.end - hazardRange.min + 1;

    if(loc && stateHazardData && countyHazardData) {
      let {attrName, unit} = getAttributeText(mode, hazardOptions[1]);
      let data = [];

      if(loc.id.length === 2) {
        if (stateHazardData[hazardOptions[1]]) {
          let currData = stateHazardData[hazardOptions[1]][loc.id] ? stateHazardData[hazardOptions[1]][loc.id].slice(startIndex, numOfYears) : [];
          let checkForData = currData.filter(arr => arr[1] > 0);
          if (checkForData.length !== 0) data = currData;
        }
      } else {
        if (Object.keys(countyHazardData).length > 0) {
          data = countyHazardData[hazardOptions[1]][loc.id] || [];
        } else {
          data = [];
        }

        if (data.length > 0) {
          
          let marker = 0;

          for (let year = hazardRange.min; year < hazardRange.max + 1; year++) {
            if (!data[marker] || data[marker][0] !== year) {
              data.push([year, 0]);
            } else {
              marker += 1;
            }
          }

          data = data.sort((a,b) => a[0] - b[0]);
          data = data.slice(startIndex, numOfYears);
        }
      }

      let options = {
        exporting:{
          ...exportObj,
          chartOptions: {
            subtitle: ''
          }
        },
        lang: {
          noData: 'No data to display',
        },
        noData: {
          style: {
            fontWeight: 'bold',
            fontSize: '15px',
            color: '#303030'
          }
        },
        legend: {enabled: false},
        credits: {enabled: false},
        chart: {
          type: 'column',
          height: chartCont.current.clientHeight,
          width: chartCont.current.clientWidth,
          backgroundColor: 'rgba(0, 0, 0, 0)'
        },
        title: {
          text: (attrName) ? `Annual ${hazardOptions[1] === 'count' ? 'Reported' : `${attrName.split(' ').slice(2).join(' ')} from`} ${hazardOptions[0]} Events in ${loc.state ? `${loc.name} County, ${loc.state}` : loc.name} from ${hazardRange.start} to ${hazardRange.end}` : ''
        },
        subtitle: {
          text: 'Click bars for episode details'
        },
        series: [
          {
            data: data,
            name: attrName
          }
        ],
        xAxis: {
          title: {
            text: 'Year'
          }
        },
        yAxis: {
          gridLineColor: '#a1a1a1',
          type: unit === '' ? 'logarithmic' : 'linear',
          title: {
            text: (unit && unit !== '') ? attrName : 'USD'
          },
          labels: {
            formatter: function() {
              let value = this.value;
              
              if (value >= 1000000000000000) {
                value = (value/1000000000000000) + 'Qd';
              } else if (value >= 1000000000000) {
                value = (value/1000000000000) + 'T';
              } else if (value >= 1000000000) {
                value = (value/1000000000) + 'B';
              } else if (value >= 1000000) {
                value = (value/1000000) + 'M';
              } else if (value >= 1000) {
                value = (value/1000) + 'k';
              }
              
              if (unit === '') {
                return '$ ' + value;
              } else {
                return value;
              }
            }
          }
        },
        tooltip: {
          pointFormatter: function() {
            let value;
            if (unit === '') {
              value = '$ ' + formatNumber(this.y);
            } else {
              value = this.y;
            }
            return `${attrName}: ${value}`;
          },
          shared: true
        },
        plotOptions: {
          column: {
            stacking: 'normal',
          },
          series: {
            cursor: 'pointer',
            events: {
              click: async function(event) {
                let episodesList;

                if(loc.id.length === 2) {
                  episodesList = stateHazardData.episodes[loc.id].filter((arr) => arr[0] === event.point.x)[0][1];
                } else {
                  episodesList = countyHazardData.episodes[loc.id].filter((arr) => arr[0] === event.point.x)[0][1];
                }

                let narratives = await getHazardNarrative(episodesList, hazardOptions);
                setEpisodes({episodes: narratives, year: event.point.x, show: true});
              }
            }
          }
        }
      };

      return <HighchartsReact highcharts={Highcharts} options={options} />;
    }
  }, [countyHazardData, stateHazardData, hazardOptions, hazardRange, loc, windowWidth, windowHeight]);


  return (
    <div id="chart" className={`card ${chartVisible ? '' : 'hidden'} ${mode ? '' : 'padded'}`}>
      {mode && <Tabs className={classes.tabsRoot} value={chartTab} onChange={handleTabChange} variant="fullWidth">
        <Tab className={classes.tabRoot} label="Historical Data"/>
        <Tab className={classes.tabRoot} label="High Emission Future" />
        <Tab className={classes.tabRoot} label="Low Emission Future" />
      </Tabs>}
      <div className="close-btn"><CloseIcon onClick={() => handleOptionsChange(!chartVisible, 'chartVisible')} /></div>

      <div id="chart-cont" className={mode ? 'tabbed' : 'untabbed'} ref={chartCont}>
        {mode && chartTab === 0 && renderClimateHistoryChart}
        {mode && chartTab !== 0 && renderClimateEmissionChart}
        {!mode && renderHazardHistoryChart}
      </div>
    </div>
  );
}

Chart.propTypes = {
  chartTab: PropTypes.number,
  chartVisible: PropTypes.bool,
  climateOption: PropTypes.string,
  climateRange: PropTypes.object,
  countyClimateData: PropTypes.object,
  countyHazardData: PropTypes.object,
  currEmData: PropTypes.object,
  emissionMinMax: PropTypes.array,
  hazardOptions: PropTypes.array,
  hazardRange: PropTypes.object,
  loc: PropTypes.object,
  mode: PropTypes.bool,
  stateClimateData: PropTypes.object,
  stateHazardData: PropTypes.object,
  windowHeight: PropTypes.number,
  windowWidth: PropTypes.number,
  downloadData: PropTypes.func,
  setEpisodes: PropTypes.func,
  handleOptionsChange: PropTypes.func
};